import { applyCapacity, capacityBoxes } from "./capacity";
import { getCSRFToken, toastFetchError } from "./htmx-helpers";
import type { AddressSuggestion } from "./address-search";

type SaveState = "" | "saving" | "saved" | "error";

const SAVED_LINGER_MS = 2400; // how long the ✓ stays before fading
const DEBOUNCE_MS = 1000;
// Only flush pending edits when navigating WITHIN the wizard, never on unrelated htmx
// traffic (chat sends use hx-post, notification polls, boosted nav).
const NAV_PATH_RE =
  /\/api\/wizards\/[^/]+\/(advance|skip|back|jump|switch-property|sync-properties)/;

// Clears the persistent "AI-filled — review" marker on a control once the
// operator edits it. Co-located with the autosave engine because the same edit
// that re-arms autosave is the one that transfers ownership machine→operator.
// The visible review dot (a .wizard-ai-mark element the wizard page injects
// next to the control) is CSS-keyed off this attribute
// ([data-ai-filled="true"] ~ .wizard-field-marks > .wizard-ai-mark), so
// deleting the attribute hides it too — no DOM cleanup needed here.
function clearAiMark(el: HTMLElement | null | undefined) {
  if (el && el.dataset.aiFilled === "true") delete el.dataset.aiFilled;
}

function isWizardNav(evt: any): boolean {
  const path = evt?.detail?.requestConfig?.path || evt?.detail?.pathInfo?.requestPath || "";
  return NAV_PATH_RE.test(path);
}

// Returns true if the response carried a flash-message error (already dispatched it).
function surfacedFlashError(resp: Response): boolean {
  const trig = resp.headers.get("HX-Trigger");
  if (!trig) return false;
  try {
    const parsed = JSON.parse(trig);
    const fm = parsed["flash-message"];
    if (fm && fm.type === "error") {
      window.dispatchEvent(new CustomEvent("flash-message", { detail: fm }));
      return true;
    }
  } catch {
    /* header wasn't JSON (simple string trigger) — ignore */
  }
  return false;
}

// Resolves an autosave response to a mark state, surfacing the server's
// explanation on the way.
//
// Every save path routes through here because the reason a save failed is often
// something only the server knows and the operator must act on — a plan limit,
// a validation rejection. The previous inline form (`resp.ok &&
// !surfacedFlashError(resp)`) short-circuited on `resp.ok === false`, so failed
// responses were never inspected at all and the operator got a bare red ✗.
//
// Two server conventions are checked, in order: an HX-Trigger flash-message
// (handlers that toast then write a status), then the JSON error body that
// HandleServiceError writes.
async function settleSave(resp: Response): Promise<SaveState> {
  if (resp.ok) return surfacedFlashError(resp) ? "error" : "saved";
  if (!surfacedFlashError(resp)) await toastFetchError(resp);
  return "error";
}

// ---------------------------------------------------------------------------
// Per-field save indicator
//
// Each autosave-wired control gets a small status mark injected into its
// wrapper (absolutely positioned at the right edge — no layout reflow). The
// engine drives it via the mark's `data-state`; CSS handles the fade + icons.
// Co-located with the field so the operator sees feedback exactly where they
// edited, regardless of how long the form is. Scoped to autosave fields only —
// inputs elsewhere in the app are untouched.
// ---------------------------------------------------------------------------
const markTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

function ensureMark(el: HTMLElement): HTMLElement {
  const existing = (el as any)._asMark as HTMLElement | undefined;
  if (existing) return existing;
  const mark = document.createElement("span");
  mark.className = "autosave-mark";
  const tag = el.tagName.toLowerCase();
  // Checkbox/radio inputs are tiny fixed-size boxes (~1.5rem). The padded
  // mark (autosave-padded → padding-right:2rem) exceeds their width and, with
  // border-box, forces the box wider than tall — a visible "stretch". They
  // don't get a padded mark: keep this span detached (never appended, no
  // padding class) so setMark() is a harmless no-op for them. Their
  // checked/selected state is its own feedback; the section/group save still
  // runs. primeMarks() passes raw inputs here, which is how step-1's language
  // checkboxes + formality radios were being distorted.
  if (tag === "input") {
    const inputType = (el as HTMLInputElement).type;
    if (inputType === "checkbox" || inputType === "radio") {
      (el as any)._asMark = mark;
      return mark;
    }
  }
  const isControl = tag === "input" || tag === "select" || tag === "textarea";
  if (tag === "textarea") mark.classList.add("autosave-mark--textarea");
  if (tag === "select") mark.classList.add("autosave-mark--select");
  mark.setAttribute("aria-hidden", "true");
  if (isControl) {
    // Form controls: our field components wrap them in a position:relative
    // div; append the mark there and reserve right padding so the value
    // never runs under the mark (constant padding => no shift).
    (el.parentElement ?? el).appendChild(mark);
    el.classList.add("autosave-padded");
  } else {
    // Composite controls (multiselect / repeater) pass a small fixed-size
    // relative slot element as the anchor; the mark fills it inline so the
    // layout never shifts.
    mark.classList.add("autosave-mark--inline");
    el.appendChild(mark);
  }
  (el as any)._asMark = mark;
  return mark;
}

// ---------------------------------------------------------------------------
// Global autosave aggregate (#140)
//
// The header status pill needs ONE signal — "is anything saving / did anything
// fail" — across all four autosave factories. Rather than instrument each
// factory's send/finally, we derive it here in setMark(), the single chokepoint
// every field save already funnels through ("saving" on start, "saved"/"error"
// on settle). Per-element contribution is tracked so the counts can't double-
// count or leak, and mirrored onto Alpine's `wizardSave` store for the header to
// bind against. "saved" and "" (the post-linger reset) both count as idle — a
// settled save decrements `pending` immediately; the ✓ linger is purely visual.
// ---------------------------------------------------------------------------
type AggState = "saving" | "error" | "idle";
let aggByEl = new WeakMap<HTMLElement, AggState>();
let aggPending = 0;
let aggErrored = 0;

function syncSaveStore() {
  const store = (window as any).Alpine?.store("wizardSave");
  if (store) {
    store.pending = aggPending;
    store.errored = aggErrored;
  }
}

// A wizard-nav body swap replaces the whole step, removing every tracked
// field. Because the counters are module-global and aggByEl is a WeakMap (we
// can't iterate it to decrement), an in-flight "saving" field that's swapped
// away never settles — its +1 leaks and the header pill sticks on "Wird
// gespeichert…" (or a phantom "Nicht gespeichert"). Resetting on nav is exact:
// the new step's freshly-primed fields start idle, so zero correctly reflects
// live saves after the swap. Re-seating aggByEl also protects the rare
// hx-preserve survivor — its stale contribution is gone, so a later settle
// reads prev="idle" and can't drive a counter negative.
function resetSaveAggregate() {
  aggPending = 0;
  aggErrored = 0;
  aggByEl = new WeakMap<HTMLElement, AggState>();
  syncSaveStore();
}

// Bound on `document` (htmx events bubble there) so it fires no matter which
// autosave component mounted on the step. htmx:afterSwap fires only on a
// successful swap, so a rejected advance — which stays on the same step and
// keeps its fields — does NOT wipe a legitimate in-flight save.
if (typeof document !== "undefined") {
  document.addEventListener("htmx:afterSwap", (e) => {
    if (isWizardNav(e)) resetSaveAggregate();
  });
}

// ---------------------------------------------------------------------------
// Deferred self-refresh for EDITABLE containers. A few wizard regions re-fetch
// themselves with a plain outerHTML swap when a server event lands (listing
// import, draft claim, PMS sync). A plain swap rebuilds the autosave form, so a
// refresh that lands mid-edit would clobber the value being typed, or render the
// selection BEFORE a debounced save persisted it. Containers opt in with
// `data-defer-refresh-while-editing`; while one is "busy" (a text control inside
// has focus, or an autosave root inside is pending / in flight — it carries
// data-autosave-busy), its GET is held via htmx:confirm, pending debounces are
// flushed, and the latest request is issued once the container is idle.
// ---------------------------------------------------------------------------
const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "hidden",
  "range",
  "color",
  "image",
]);

function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  if ((el as HTMLElement).isContentEditable) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === "textarea") return true;
  if (tag === "input") return !NON_TEXT_INPUT_TYPES.has((el as HTMLInputElement).type);
  return false;
}

export function refreshBlocked(container: Element): boolean {
  const active = document.activeElement;
  if (active && container.contains(active) && isTextEntry(active)) return true;
  return container.querySelector("[data-autosave-busy]") !== null;
}

const DEFER_POLL_MS = 300;
const deferredRefresh = new WeakMap<Element, (skipConfirm?: boolean) => unknown>();

// htmx:confirm handler. Returns true when it held the request back.
export function deferRefreshWhileEditing(evt: Event): boolean {
  const d = (evt as CustomEvent).detail || {};
  const elt = d.elt as Element | undefined;
  if (!elt || !elt.hasAttribute?.("data-defer-refresh-while-editing")) return false;
  if (typeof d.verb === "string" && d.verb.toLowerCase() !== "get") return false;
  if (typeof d.issueRequest !== "function" || !refreshBlocked(elt)) return false;
  evt.preventDefault();
  // Push pending debounces out now so "busy" ends as soon as the saves land.
  elt
    .querySelectorAll("[data-autosave-busy]")
    .forEach((el) => el.dispatchEvent(new CustomEvent("wizard:autosave-flush")));
  const alreadyWaiting = deferredRefresh.has(elt);
  deferredRefresh.set(elt, d.issueRequest); // coalesce: only the latest trigger runs
  if (alreadyWaiting) return true;
  const iv = setInterval(() => {
    if (!elt.isConnected) {
      clearInterval(iv);
      deferredRefresh.delete(elt);
      return;
    }
    if (refreshBlocked(elt)) return;
    clearInterval(iv);
    const issue = deferredRefresh.get(elt);
    deferredRefresh.delete(elt);
    issue?.(true);
  }, DEFER_POLL_MS);
  return true;
}

if (typeof document !== "undefined") {
  document.addEventListener("htmx:confirm", deferRefreshWhileEditing);
}

function syncBusyFlag(root: HTMLElement | null | undefined, busy: boolean) {
  if (!root) return;
  if (busy) root.dataset.autosaveBusy = "1";
  else delete root.dataset.autosaveBusy;
}

function updateSaveAggregate(el: HTMLElement, state: SaveState) {
  const prev = aggByEl.get(el) ?? "idle";
  const next: AggState = state === "saving" ? "saving" : state === "error" ? "error" : "idle";
  if (prev === next) return;
  if (prev === "saving") aggPending--;
  else if (prev === "error") aggErrored--;
  if (next === "saving") aggPending++;
  else if (next === "error") aggErrored++;
  aggByEl.set(el, next);
  syncSaveStore();
}

function setMark(el: HTMLElement | null | undefined, state: SaveState) {
  if (!el) return;
  updateSaveAggregate(el, state);
  const mark = ensureMark(el);
  const prev = markTimers.get(el);
  if (prev) {
    clearTimeout(prev);
    markTimers.delete(el);
  }
  mark.dataset.state = state;
  if (state === "saved") {
    // Fade the ✓ away after a beat so it doesn't linger as visual noise.
    markTimers.set(
      el,
      setTimeout(() => {
        mark.dataset.state = "";
      }, SAVED_LINGER_MS),
    );
  }
}

// Pre-create marks (reserving padding) for every autosave control under `root`
// so the first save never shifts the field.
function primeMarks(controls: Iterable<Element>) {
  for (const c of controls) ensureMark(c as HTMLElement);
}

// ---------------------------------------------------------------------------
// Per-field autosave (Brand, Property Facts scalar fields). Each field carries
// data-autosave-name + data-autosave-url and is PATCHed as {field, value}.
// data-autosave-suppress (set by the wizard_field_filled listener) prevents an
// LLM fill from re-arming a human autosave.
// ---------------------------------------------------------------------------
export function wizardAutosave() {
  return {
    _timers: {} as Record<string, ReturnType<typeof setTimeout>>,
    _inFlight: {} as Record<string, boolean>,
    // Latest value requested while that field's save was in flight — sent as a
    // trailing save when it settles (never dropped; last write wins).
    _pending: {} as Record<
      string,
      { url: string; value: string; anchor: HTMLElement; extra: Record<string, string> }
    >,
    _byName: {} as Record<string, HTMLElement>,
    _root: null as HTMLElement | null,
    // Removes the listeners init() put on long-lived targets (window,
    // document.body). Set by init(), consumed by destroy(). See destroy().
    _detachGlobal: null as null | (() => void),

    init() {
      const root = (this as any).$el as HTMLElement;
      this._root = root;
      // Defensive: a re-init of the same instance must not stack a second set.
      this._detachGlobal?.();
      const fields = root.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >("[data-autosave-name]");
      primeMarks(fields);
      fields.forEach((f) => {
        const name = f.getAttribute("data-autosave-name")!;
        this._byName[name] = f;
        const tag = f.tagName.toLowerCase();
        // data-autosave-blur opts a text field out of debounced-while-typing
        // saves and into the change-based branch (fires on blur/Enter, only
        // when the value changed). For format-validated fields (social
        // profile URLs/@handles) the debounce PATCHes half-typed values the
        // server rightly rejects — an error toast mid-typing for input the
        // user hasn't finished is worse than a slightly later checkmark.
        const debounced =
          !f.hasAttribute("data-autosave-blur") &&
          (tag === "textarea" || (tag === "input" && (f as HTMLInputElement).type === "text"));
        const guard = (fn: () => void) => () => {
          // Machine fill (set by onFieldFilled) — not a human edit: skip
          // both the autosave re-arm AND the AI-marker clear.
          if ((f as HTMLElement).dataset.autosaveSuppress) {
            delete (f as HTMLElement).dataset.autosaveSuppress;
            return;
          }
          // Genuine operator edit: this value is now theirs, so the
          // persistent AI-filled marker must clear immediately (the DB
          // owner→operator transition rides the autosave PATCH).
          clearAiMark(f as HTMLElement);
          fn();
        };
        if (debounced) {
          f.addEventListener(
            "input",
            guard(() => this._queue(f)),
          );
          f.addEventListener("blur", () => this._flush(f));
        } else {
          f.addEventListener(
            "change",
            guard(() => this._save(f)),
          );
        }
      });
      // Listeners on window/document.body outlive this component: they are
      // removed in destroy() via _detachGlobal. Root/field listeners die with
      // the element and need no cleanup.
      const onUnload = () => this._flushDirty(fields);
      const onBeforeRequest = (e: Event) => {
        if (isWizardNav(e)) this._flushDirty(fields);
      };
      const onCancelPending = (e: any) => this.cancelPending(e.detail.field);
      const body = document.body;
      window.addEventListener("beforeunload", onUnload);
      body.addEventListener("htmx:beforeRequest", onBeforeRequest);
      window.addEventListener("wizard:cancel-pending", onCancelPending);
      this._detachGlobal = () => {
        window.removeEventListener("beforeunload", onUnload);
        body.removeEventListener("htmx:beforeRequest", onBeforeRequest);
        window.removeEventListener("wizard:cancel-pending", onCancelPending);
      };
      // A deferred container refresh (deferRefreshWhileEditing) asks busy
      // roots to push their debounced edits out immediately.
      root.addEventListener("wizard:autosave-flush", () => this._flushDirty(fields));
      // Composite controls (multiselect, repeater) serialize their own value
      // and dispatch this event; they save through the same path + endpoint.
      root.addEventListener("wizard:autosave-field", (e: any) => {
        const { name, value, anchor } = e.detail || {};
        const url = e.detail?.url || root.dataset.autosaveUrl;
        if (!name || !url) return;
        let extra: Record<string, string> = {};
        try {
          if (root.dataset.autosaveExtra) extra = JSON.parse(root.dataset.autosaveExtra);
        } catch {}
        this.saveField(
          name,
          url,
          value,
          (anchor as HTMLElement) || (e.target as HTMLElement),
          extra,
        );
      });
    },

    // Alpine 3 calls destroy() when the x-data element leaves the DOM (step
    // change body swap, outerHTML refresh of the step container).
    //
    // Semantics: FLUSH, never drop. A still-debouncing edit is saved now so
    // the last keystroke reaches the server. In the common paths this is a
    // no-op — wizard nav already flushed on htmx:beforeRequest and a deferred
    // refresh already flushed via wizard:autosave-flush, both of which clear
    // _timers — so destroy only saves what no earlier hook caught (and never
    // saves twice). In-flight saves and their queued trailing save are left to
    // settle on their own. The save runs against the detached field, which
    // still holds the value; its mark update is invisible but harmless.
    //
    // Components that spread this one (propertyLocationAutosave) inherit this
    // destroy(); one that defines its own must call base.destroy.call(this).
    destroy() {
      for (const name of Object.keys(this._timers)) {
        const f = this._byName[name];
        if (f) this._flush(f);
        else {
          clearTimeout(this._timers[name]);
          delete this._timers[name];
        }
      }
      this._detachGlobal?.();
      this._detachGlobal = null;
    },

    // Mirrors "has a debounced edit or a save in flight" onto the root as
    // data-autosave-busy, which deferRefreshWhileEditing reads.
    _syncBusy() {
      syncBusyFlag(
        this._root,
        Object.keys(this._timers).length > 0 ||
          Object.values(this._inFlight).some(Boolean) ||
          Object.keys(this._pending).length > 0,
      );
    },
    _queue(f: HTMLElement) {
      const name = f.getAttribute("data-autosave-name")!;
      if (this._timers[name]) clearTimeout(this._timers[name]);
      this._timers[name] = setTimeout(() => this._save(f), DEBOUNCE_MS);
      this._syncBusy();
    },
    _flush(f: HTMLElement) {
      const name = f.getAttribute("data-autosave-name")!;
      if (!this._timers[name]) return;
      clearTimeout(this._timers[name]);
      delete this._timers[name];
      this._save(f);
    },
    _flushDirty(fields: NodeListOf<Element>) {
      fields.forEach((f) => this._flush(f as HTMLElement));
    },
    cancelPending(name: string) {
      if (this._timers[name]) {
        clearTimeout(this._timers[name]);
        delete this._timers[name];
      }
      this._syncBusy();
    },

    // Imperative save for composite fields (multiselect / repeater) that
    // serialize their own value. Caller passes the anchor element to mark.
    saveField(
      name: string,
      url: string,
      value: string,
      anchor: HTMLElement,
      extra: Record<string, string> = {},
    ) {
      this._send(name, url, value, anchor, extra);
    },

    async _save(f: HTMLElement) {
      const el = f as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      const name = el.getAttribute("data-autosave-name")!;
      const url = el.getAttribute("data-autosave-url")!;
      if (this._timers[name]) {
        clearTimeout(this._timers[name]);
        delete this._timers[name];
      }
      const value =
        (el as HTMLInputElement).type === "checkbox"
          ? String((el as HTMLInputElement).checked)
          : el.value;
      let extra: Record<string, string> = {};
      try {
        const rootEl = el.closest("[data-autosave-extra]") as HTMLElement | null;
        if (rootEl?.dataset.autosaveExtra) extra = JSON.parse(rootEl.dataset.autosaveExtra);
      } catch {}
      this._send(name, url, value, el, extra);
      this._syncBusy();
    },

    async _send(
      name: string,
      url: string,
      value: string,
      anchor: HTMLElement,
      extra: Record<string, string>,
    ) {
      if (this._inFlight[name]) {
        this._pending[name] = { url, value, anchor, extra };
        this._syncBusy();
        return;
      }
      this._inFlight[name] = true;
      this._syncBusy();
      setMark(anchor, "saving");
      try {
        const resp = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ ...extra, field: name, value }),
        });
        const state = await settleSave(resp);
        setMark(anchor, state);
        if (state === "saved") {
          window.dispatchEvent(new CustomEvent("autosave-saved", { detail: { field: name } }));
        }
      } catch {
        setMark(anchor, "error");
      } finally {
        this._inFlight[name] = false;
        const next = this._pending[name];
        delete this._pending[name];
        if (next) this._send(name, next.url, next.value, next.anchor, next.extra);
        this._syncBusy();
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Property location autosave (Step 2 per-property location block,
// templates/components/property_location_fields.templ). A superset of
// wizardAutosave: the six address fields still autosave one-at-a-time via
// data-autosave-name/data-autosave-url exactly like any other wizardAutosave
// root (a single-field edit through the "Details" disclosure), but an
// `address-selected` event bubbling up from the nested AddressSearch
// combobox is handled here as ONE batched PATCH to
// data-location-autosave-url instead — the fix for the lost-update defect
// (design doc §6): six concurrent per-field PATCHes for one selection each
// do their own whole-row read-modify-write against the SAME pre-selection
// snapshot, so the last writer's save silently reverts the other three
// address columns.
//
// CRITICAL: the six inputs are filled with a plain `el.value = …` — never a
// synthesised `input`/`change` event. wizardAutosave's own per-field
// listeners (wired by the borrowed init() below) bind to those DOM events;
// dispatching them here would fan this single batch write back out into six
// per-field PATCHes and reinstate the exact defect this component exists to
// fix. If a future change genuinely needs the events, set
// `data-autosave-suppress` on the element first (see onFieldFilled's own
// convention above) rather than firing them unconditionally.
// ---------------------------------------------------------------------------

// Result/match granularities the coarse-warning gate treats as "precise
// enough" — mirrors design doc §6 ("Behaviour"): gate on result_type /
// match_type, NEVER on rank.confidence (F6).
const PRECISE_RESULT_TYPES = new Set(["building", "street", "amenity"]);

export function propertyLocationAutosave(initialStatus: "missing" | "resolved" = "missing") {
  // Compose with wizardAutosave() via spread — NOT just `.init.call(this)`.
  // wizardAutosave's init() wires listeners whose closures call `this._queue`,
  // `this._flush`, `this._save`, `this._flushDirty`, `this.cancelPending`,
  // `this.saveField` (see wizardAutosave above). Calling only `.init.call(this)`
  // without also bringing those methods onto this object left every one of
  // them `undefined` — a REAL browser bug (TypeError on the first keystroke,
  // on every htmx:beforeRequest, and on beforeunload) that no Go test can see
  // and that `tsc`/Vite do not flag either (see the task-5 report's fix-round
  // falsification). Spreading `base` brings _queue/_flush/_save/_send/
  // _flushDirty/cancelPending/saveField along, so calling `base.init.call(this)`
  // below actually resolves to real methods on `this`.
  const base = wizardAutosave();
  return {
    ...base,

    // Status-pill state (design doc §6 "Behaviour" / Step 6). Declared AFTER
    // the spread so these two fields — unique to this component — aren't
    // shadowed by anything in base (base has neither key, but this ordering
    // keeps future edits to wizardAutosave from silently winning here).
    locationStatus: initialStatus as "missing" | "resolved" | "stale",
    coarseWarning: false,

    init() {
      const root = (this as any).$el as HTMLElement;

      // Whole-branch review Fix 10: this loop MUST run BEFORE
      // base.init.call(this) below. wizardAutosave's own per-field listener
      // (the `guard` closure) is a ONE-SHOT consumer of data-autosave-suppress
      // — the moment it sees the flag set (an LLM/research auto-fill via
      // onFieldFilled dispatching a synthetic input event), it DELETES the
      // flag and returns. Same-element listeners fire in registration order
      // regardless of capture/bubble phase, so if `flip` were registered
      // AFTER guard (as it used to be — right after base.init.call(this)),
      // guard's listener would already have cleared the flag by the time
      // flip's own `if (el.dataset.autosaveSuppress) return` check ran,
      // misreading every machine fill as a genuine operator edit and wrongly
      // flipping the resolved pill to "stale". Querying the fields directly
      // here (rather than via this._byName, which base.init populates and
      // isn't available yet) sidesteps that ordering dependency entirely.
      const fields = root.querySelectorAll<HTMLElement>("[data-autosave-name]");
      fields.forEach((el) => {
        // A genuine operator edit to any of the six fields after a match
        // means the coordinates may no longer describe the (now-different)
        // address. Flip the pill to stale — NEVER clear lat/lng here or
        // anywhere else in this component (design doc §6: "Coordinates are
        // never silently cleared").
        const flip = () => {
          if (el.dataset.autosaveSuppress) return; // machine fill, not an operator edit
          if (this.locationStatus !== "missing") this.locationStatus = "stale";
        };
        el.addEventListener("input", flip);
        el.addEventListener("change", flip);
      });

      // Borrow wizardAutosave's per-field wiring (debounce/blur/change,
      // save marks, AI-mark clearing) unmodified, bound to THIS component's
      // own `this` (which now — via the spread above — actually HAS
      // _queue/_flush/_save/etc. for that wiring to call).
      base.init.call(this);

      // No destroy() override: the spread above inherits base's destroy(),
      // which removes the window/body listeners base.init wired onto `this`
      // and flushes pending debounces. Everything added here is on the root
      // or its fields and dies with them. If you add a global listener here,
      // define destroy() and chain base.destroy.call(this).
      root.addEventListener("address-selected", (e: Event) =>
        this._onAddressSelected(e as CustomEvent),
      );
    },

    async _onAddressSelected(e: CustomEvent<AddressSuggestion>) {
      const root = (this as any).$el as HTMLElement;
      const url = root.dataset.locationAutosaveUrl;
      const detail = e.detail;
      if (!url || !detail) return;

      // The Property model has one street line (address_line1) — no separate
      // house-number column — so house_number is folded into it here.
      const street = [detail.street, detail.house_number].filter(Boolean).join(" ").trim();
      const values: Record<string, string> = {
        street,
        postal_code: detail.postal_code || "",
        // Native-language city name (Geoapify's geocode endpoints no longer
        // force lang=de) — correct in every locale, unlike a German exonym.
        city: detail.city || "",
        // ISO-3166-1 alpha-2 code, NOT detail.country (a localized display
        // name like "Italien"/"Italy"/"Italia" depending on upstream
        // language). domain.NormalizeCountryCode only recognizes German
        // exonyms for DE/AT/CH/FR/IT — every other country's localized name
        // normalized to "", silently losing the country. The ISO code has no
        // such gap and needs no exonym table at all. See CLAUDE.md's geocode
        // defect writeup.
        country: (detail.country_code || "").toUpperCase(),
        latitude: detail.lat != null ? String(detail.lat) : "",
        longitude: detail.lng != null ? String(detail.lng) : "",
      };

      for (const [name, value] of Object.entries(values)) {
        const el = this._byName[name] as HTMLInputElement | undefined;
        if (el) el.value = value;
      }

      let extra: Record<string, string> = {};
      try {
        if (root.dataset.autosaveExtra) extra = JSON.parse(root.dataset.autosaveExtra);
      } catch {
        /* malformed data-autosave-extra — send without it rather than throw */
      }

      const marks = Object.values(this._byName) as HTMLElement[];
      marks.forEach((el) => setMark(el, "saving"));
      try {
        const resp = await fetch(url, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ ...extra, ...values }),
        });
        const state = await settleSave(resp);
        marks.forEach((el) => setMark(el, state));
        if (state !== "error") {
          this.locationStatus = "resolved";
          this.coarseWarning = !PRECISE_RESULT_TYPES.has(detail.result_type);
          // P1's hub sidebar and P2's location hint (PropertyLocationHint)
          // re-fetch on this event; per-field saves already dispatch it in _send.
          window.dispatchEvent(new CustomEvent("autosave-saved", { detail: { field: "location" } }));
        }
      } catch {
        marks.forEach((el) => setMark(el, "error"));
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Create-accommodation modal location (step 2 "add new accommodation" modal,
// templates/components/wizard/step_properties.templ → wizardAddPropertyLocationBlock).
//
// Unlike propertyLocationAutosave there is NO property to PATCH yet — the
// property is created by the modal's own form submit. This root only mirrors
// an AddressSearch selection into hidden form inputs (via x-model) so the six
// location fields submit alongside name/type/urls, letting HandleAddProperty
// create the property WITH coordinates in one shot (issue #137). Field mapping
// is identical to _onAddressSelected above: house_number folded into street,
// ISO country_code (never the localized display name), lat/lng stringified.
// ---------------------------------------------------------------------------
export function addAccommodationLocation() {
  return {
    street: "",
    postal_code: "",
    city: "",
    country: "",
    latitude: "",
    longitude: "",
    // Non-empty once a suggestion is chosen — drives the confirmation chip.
    resolved: "",

    onAddressSelected(e: CustomEvent<AddressSuggestion>) {
      const d = e.detail;
      if (!d) return;
      this.street = [d.street, d.house_number].filter(Boolean).join(" ").trim();
      this.postal_code = d.postal_code || "";
      this.city = d.city || "";
      // ISO-3166-1 alpha-2, NOT d.country (a localized display name). See the
      // country_code rationale in _onAddressSelected above.
      this.country = (d.country_code || "").toUpperCase();
      this.latitude = d.lat != null ? String(d.lat) : "";
      this.longitude = d.lng != null ? String(d.lng) : "";
      this.resolved = d.formatted || "";
    },

    // Reset both this root's fields AND the nested AddressSearch combobox.
    // Called on the wizard-property-added success event (the modal is reused
    // across adds and the native form.reset() does not touch Alpine state).
    clear() {
      this.street = "";
      this.postal_code = "";
      this.city = "";
      this.country = "";
      this.latitude = "";
      this.longitude = "";
      this.resolved = "";
      // AddressSearch owns its `query` in a separate Alpine root; form.reset()
      // leaves it stale. Clearing the DOM value and firing a native `input`
      // event drives addressSearch.onInput, which syncs query back to "" and
      // closes the dropdown.
      const root = (this as any).$root as HTMLElement | undefined;
      const search = root?.querySelector<HTMLInputElement>('input[name$="address_search"]');
      if (search) {
        search.value = "";
        search.dispatchEvent(new Event("input", { bubbles: true }));
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Property-selection autosave (step 2 checkboxes). POSTs the full checked set.
// The ✓ shows on the row of the checkbox you just toggled.
// ---------------------------------------------------------------------------
// `limit` is the PLAN's accommodation cap for the whole site (the counter's
// denominator, -1 for unlimited); `locked` is how much of it is already spent by
// rows the operator cannot untick — an add-accommodation run's already-published
// baseline. The selectable budget is the difference, so the counter reads as the
// operator's real allowance rather than a per-step number they'd have to
// reconcile against the site themselves.
export function wizardSelectionAutosave(url: string, limit = -1, locked = 0) {
  return {
    _timer: null as ReturnType<typeof setTimeout> | null,
    _inFlight: false,
    // A save was requested while one was in flight: re-save the (then
    // current) checkbox state when it settles instead of dropping it.
    _again: false,
    _lastToggled: null as HTMLElement | null,
    // Capacity contract consumed by CapacitySelectionHint.
    limit,
    locked,
    used: 0,
    full: false,
    _onSettle: null as null | (() => void),

    _root: null as HTMLElement | null,

    init() {
      const root = (this as any).$el as HTMLElement;
      this._root = root;
      if (this._onSettle) document.body.removeEventListener("htmx:afterSettle", this._onSettle);
      this._syncCapacity();
      root.addEventListener("change", (e) => {
        const t = e.target as HTMLInputElement;
        if (t && t.name === "selected_property_ids" && t.type === "checkbox") {
          this._lastToggled = (t.closest("label") as HTMLElement) ?? t;
          this._syncCapacity();
          this._queue(root);
        }
      });
      // Deferred step refresh (deferRefreshWhileEditing): save a debounced
      // toggle NOW so the re-render reflects it instead of racing it.
      root.addEventListener("wizard:autosave-flush", () => {
        if (this._timer) this._save(root);
      });
      // The step body is re-rendered (outerHTML swap) whenever a draft is claimed,
      // a listing is added, or a PMS sync lands — each of which can add or
      // pre-check rows without any `change` event firing. Recount after every
      // settle so the counter and the locked rows describe the list actually on
      // screen, not the one this component initialised against.
      this._onSettle = () => {
        if (root.isConnected) this._syncCapacity();
      };
      document.body.addEventListener("htmx:afterSettle", this._onSettle);
    },

    // Flush (not drop) a still-debouncing toggle — see wizardAutosave.destroy
    // for the rationale — then unhook the body listener.
    destroy() {
      if (this._timer && this._root) this._save(this._root);
      if (this._onSettle) document.body.removeEventListener("htmx:afterSettle", this._onSettle);
      this._onSettle = null;
    },

    _syncCapacity() {
      const root = (this as any).$el as HTMLElement;
      const selectable = this.limit < 0 ? -1 : Math.max(0, this.limit - this.locked);
      const state = applyCapacity(capacityBoxes(root, "selected_property_ids"), selectable);
      // The counter reports the plan-level total (locked baseline + this run's
      // picks), which is what the operator's cap actually measures.
      this.used = state.used + this.locked;
      this.full = state.full;
    },
    _syncBusy() {
      syncBusyFlag(this._root, this._timer !== null || this._inFlight || this._again);
    },
    _queue(root: HTMLElement) {
      if (this._timer) clearTimeout(this._timer);
      this._timer = setTimeout(() => this._save(root), 800);
      this._syncBusy();
    },
    async _save(root: HTMLElement) {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
      if (this._inFlight) {
        this._again = true;
        this._syncBusy();
        return;
      }
      this._syncBusy();
      const ids = Array.from(
        root.querySelectorAll<HTMLInputElement>('input[name="selected_property_ids"]:checked'),
      ).map((i) => i.value);
      this._inFlight = true;
      this._syncBusy();
      setMark(this._lastToggled, "saving");
      try {
        const resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ property_ids: ids }),
        });
        setMark(this._lastToggled, await settleSave(resp));
      } catch {
        setMark(this._lastToggled, "error");
      } finally {
        this._inFlight = false;
        if (this._again) {
          this._again = false;
          this._save(root);
        }
        this._syncBusy();
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Per-section autosave (Booking, Marketing×3, Notes, Legal). Debounces on any
// input, POSTs the whole form. The ✓ shows on the field you last edited.
// ---------------------------------------------------------------------------
export function wizardSectionAutosave() {
  return {
    _timer: null as ReturnType<typeof setTimeout> | null,
    _inFlight: false,
    // A save was requested while one was in flight: re-post the (then
    // current) form when it settles instead of dropping the edit.
    _again: false,
    _dirty: false,
    _lastEdited: null as HTMLElement | null,
    _form: null as HTMLFormElement | null,
    // Removes init()'s window/document.body listeners; see destroy().
    _detachGlobal: null as null | (() => void),

    init() {
      const root = (this as any).$el as HTMLElement;
      const form = (root.tagName === "FORM" ? root : root.querySelector("form")) as HTMLFormElement;
      if (!form) return;
      this._detachGlobal?.();
      this._form = form;
      primeMarks(form.querySelectorAll('input:not([type="hidden"]), textarea, select'));
      const onEdit = (e: Event) => {
        const t = e.target as HTMLElement;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) {
          // A live marketing fill arrives via onFieldFilled, which sets
          // data-autosave-suppress then dispatches a synthetic input.
          // That synthetic event bubbles to this form listener too. It is
          // NOT a human edit and the value is ALREADY persisted (the LLM
          // tool / research job wrote it to the DB before emitting the SSE),
          // so DO NOT queue an autosave: re-posting the whole section form
          // here would submit stale list-field state (repeaters aren't
          // live-filled) and overwrite the freshly-written values — exactly
          // the clobber that emptied a property's differentiators when it
          // was the one on screen during an assistant fill.
          if (t.dataset.autosaveSuppress) {
            delete t.dataset.autosaveSuppress;
            return;
          }
          this._lastEdited = t;
          clearAiMark(t);
        }
        this._queue(form);
      };
      form.addEventListener("input", onEdit);
      form.addEventListener("change", onEdit);
      const onUnload = () => {
        if (this._dirty) this._save(form);
      };
      const onBeforeRequest = (e: Event) => {
        if (this._dirty && isWizardNav(e)) this._save(form);
      };
      const body = document.body;
      window.addEventListener("beforeunload", onUnload);
      body.addEventListener("htmx:beforeRequest", onBeforeRequest);
      this._detachGlobal = () => {
        window.removeEventListener("beforeunload", onUnload);
        body.removeEventListener("htmx:beforeRequest", onBeforeRequest);
      };
    },
    // Flush (not drop) a still-debouncing edit — see wizardAutosave.destroy
    // for the rationale — then unhook the window/body listeners. Only the
    // pending-debounce case saves: a failed save leaves _dirty set with no
    // timer, and destroy must not turn element removal into a blind retry.
    destroy() {
      if (this._timer && this._form) this._save(this._form);
      this._detachGlobal?.();
      this._detachGlobal = null;
    },
    _queue(form: HTMLFormElement) {
      this._dirty = true;
      if (this._timer) clearTimeout(this._timer);
      this._timer = setTimeout(() => this._save(form), DEBOUNCE_MS);
    },
    async _save(form: HTMLFormElement) {
      if (this._timer) {
        clearTimeout(this._timer);
        this._timer = null;
      }
      if (this._inFlight) {
        this._again = true;
        return;
      }
      const url =
        form.dataset.autosaveUrl || form.getAttribute("hx-post") || form.getAttribute("hx-patch")!;
      const method =
        form.dataset.autosaveMethod ||
        (form.dataset.autosaveUrl ? "PATCH" : form.getAttribute("hx-patch") ? "PATCH" : "POST");
      this._inFlight = true;
      this._dirty = false;
      setMark(this._lastEdited, "saving");
      try {
        // URLSearchParams => application/x-www-form-urlencoded so Go's
        // r.ParseForm()/r.FormValue() parses the body (FormData sends
        // multipart, which ParseForm won't re-parse).
        const body = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
        const resp = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": getCSRFToken(),
          },
          body,
        });
        const state = await settleSave(resp);
        setMark(this._lastEdited, state);
        if (state === "error") this._dirty = true;
      } catch {
        setMark(this._lastEdited, "error");
        this._dirty = true;
      } finally {
        this._inFlight = false;
        if (this._again) {
          this._again = false;
          this._save(form);
        }
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Attractions repeater fill merge (website_property_location_form.templ /
// attractionsFillListener). Extracted to a pure function so the matching
// logic is unit-testable — see wizard-autosave.test.ts.
// ---------------------------------------------------------------------------

// The shape a wizard_field_filled SSE payload carries for
// location_research.attractions (mirrors attractionSSE in
// internal/llm/tools/property_writer.go). Never carries lat/lng — only the
// CMS geocoding editor (Task B4) writes those.
export interface AttractionFillRow {
  id?: string;
  name?: string;
  type?: string;
  distance?: string;
  notes?: string;
}

// The repeater's live row shape (mirrors attractionRow in
// website_property_location_form.templ). lat/lng are always strings ("" =
// absent, never undefined/null — see attractionRow's doc comment on why).
export interface AttractionFillTarget {
  id: string;
  name: string;
  type: string;
  distance: string;
  notes: string;
  lat: string;
  lng: string;
  _geoResolved?: boolean;
}

// mergeAttractionsFill applies a research/LLM fill payload onto the
// repeater's current rows, carrying forward any coordinates the operator
// already geocoded (Task B4) instead of letting the fill's wholesale reset
// silently wipe them.
//
// Matching strategy: by `id`, ONLY. property_writer.go's
// mergeLocationResearch calls LocationResearch.EnsureAttractionIDs — which
// assigns a stable id to EVERY attraction (existing rows keep theirs, brand
// new ones get a freshly-computed one) — BEFORE building the SSE payload, so
// every fill row always carries an id. That means an id lookup MISS (the
// fill row's id isn't in the client's `existing` map) reliably means "this
// is a new attraction", never "this row has no id" — the two must not be
// conflated. If a fill row's id genuinely doesn't match anything, treat it
// as new; do NOT fall through to a name-based guess, or a new attraction
// whose name happens to collide with a different, already-geocoded existing
// row (generic names: "Park", "Supermarkt", "Spielplatz") would silently
// inherit that OTHER row's coordinates — a misattribution that reads as
// confidently "resolved" pointing at the wrong place, worse than a wipe.
//
// The name-based fallback (trimmed, case-insensitive) applies ONLY when a
// row has NO id at all on either side — a defensive path for a
// hand-rolled/older payload that predates EnsureAttractionIDs, not something
// that should occur in normal operation. `byName` keeps only the first
// existing row per name; that arbitrary pick is acceptable exactly because
// this path is defensive-only, not the primary matching strategy.
//
// A fill row that matches nothing existing is a genuinely new attraction:
// its lat/lng default to "" (never undefined), so the status pill's
// `attr.lat !== ''` check reads it as "missing" rather than "manually
// entered".
export function mergeAttractionsFill(
  existing: AttractionFillTarget[] | undefined,
  incoming: AttractionFillRow[] | undefined,
): AttractionFillTarget[] {
  const rows = Array.isArray(existing) ? existing : [];
  const fills = Array.isArray(incoming) ? incoming : [];

  const byId = new Map<string, AttractionFillTarget>();
  const byName = new Map<string, AttractionFillTarget>();
  for (const row of rows) {
    if (row && row.id) byId.set(row.id, row);
    const nameKey = (row?.name || "").trim().toLowerCase();
    if (row && nameKey && !byName.has(nameKey)) byName.set(nameKey, row);
  }

  return fills.map((fill) => {
    const id = fill.id || "";
    const nameKey = (fill.name || "").trim().toLowerCase();
    // id present → id lookup is authoritative, whether it hits or misses.
    // id absent → defensive name fallback.
    const match = id ? byId.get(id) : nameKey ? byName.get(nameKey) : undefined;
    return {
      id,
      name: fill.name || "",
      type: fill.type || "",
      distance: fill.distance || "",
      notes: fill.notes || "",
      lat: match?.lat || "",
      lng: match?.lng || "",
      _geoResolved: match?._geoResolved,
    };
  });
}
